import React, { useState, useEffect } from "react";
import {
  Ticket,
  Plus,
  Search,
  Edit,
  Trash2,
  Power,
  PowerOff,
  Filter,
  Calendar,
  Percent,
} from "lucide-react";
import { Card, CardContent, CardHeader, CardTitle } from "../ui/card";
import { Button } from "../ui/button";
import { Input } from "../ui/input";
import { Badge } from "../ui/badge";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "../ui/table";
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from "../ui/alert-dialog";
import { Dialog, DialogContent } from "../ui/dialog";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "../ui/select";
import CouponFormModal from "./CouponFormModal";
import couponService from "../../services/couponService";
import { showError, showInfo } from "../../store/errorStore";

const CouponsManagement = () => {
  const [coupons, setCoupons] = useState([]);
  const [loading, setLoading] = useState(true);
  const [searchQuery, setSearchQuery] = useState("");
  const [showModal, setShowModal] = useState(false);
  const [editingCoupon, setEditingCoupon] = useState(null);
  const [savingCoupon, setSavingCoupon] = useState(false);
  const [showDeleteDialog, setShowDeleteDialog] = useState(false);
  const [deletingCouponId, setDeletingCouponId] = useState(null);
  const [filterStatus, setFilterStatus] = useState("all");

  useEffect(() => {
    loadCoupons();
  }, []);

  const loadCoupons = async () => {
    try {
      setLoading(true);
      const data = await couponService.getAllCoupons();
      setCoupons(Array.isArray(data) ? data : []);
    } catch (error) {
      showError("Erro ao carregar cupons");
      setCoupons([]);
    } finally {
      setLoading(false);
    }
  };

  const handleCreateCoupon = () => {
    setEditingCoupon(null);
    setShowModal(true);
  };

  const handleEditCoupon = (coupon) => {
    setEditingCoupon(coupon);
    setShowModal(true);
  };

  const handleSaveCoupon = async (couponData) => {
    try {
      setSavingCoupon(true);

      if (editingCoupon) {
        await couponService.updateCoupon(editingCoupon.id, couponData);
        showInfo("Cupom atualizado com sucesso!");
      } else {
        await couponService.createCoupon(couponData);
        showInfo("Cupom criado com sucesso!");
      }

      await loadCoupons();
      setShowModal(false);
      setEditingCoupon(null);
    } catch (error) {
      showError(error.response?.data?.message || "Erro ao salvar cupom");
    } finally {
      setSavingCoupon(false);
    }
  };

  const handleDeleteClick = (coupon) => {
    setDeletingCouponId(coupon.id);
    setShowDeleteDialog(true);
  };

  const handleDeleteConfirm = async () => {
    try {
      await couponService.deleteCoupon(deletingCouponId);
      showInfo("Cupom excluído com sucesso!");
      await loadCoupons();
    } catch (error) {
      showError("Erro ao excluir cupom");
    } finally {
      setShowDeleteDialog(false);
      setDeletingCouponId(null);
    }
  };

  const handleToggleActive = async (coupon) => {
    try {
      if (coupon.active) {
        await couponService.deactivateCoupon(coupon.id);
        showInfo("Cupom desativado com sucesso!");
      } else {
        await couponService.activateCoupon(coupon.id);
        showInfo("Cupom ativado com sucesso!");
      }
      await loadCoupons();
    } catch (error) {
      showError("Erro ao alterar status do cupom");
    }
  };

  // Filtrar cupons
  const filteredCoupons = coupons.filter((coupon) => {
    const matchesSearch =
      coupon.code?.toLowerCase().includes(searchQuery.toLowerCase()) ||
      coupon.description?.toLowerCase().includes(searchQuery.toLowerCase());

    const matchesStatus =
      filterStatus === "all" ||
      (filterStatus === "active" &&
        coupon.active &&
        !couponService.isExpired(coupon.expiration_date)) ||
      (filterStatus === "inactive" && !coupon.active) ||
      (filterStatus === "expired" &&
        couponService.isExpired(coupon.expiration_date));

    return matchesSearch && matchesStatus;
  });

  // Estatísticas
  const stats = {
    total: coupons.length,
    active: coupons.filter(
      (c) => c.active && !couponService.isExpired(c.expiration_date)
    ).length,
    inactive: coupons.filter((c) => !c.active).length,
    expired: coupons.filter((c) => couponService.isExpired(c.expiration_date))
      .length,
  };

  return (
    <div className="space-y-6">
      {/* Modal de Loading */}
      <Dialog open={loading} onOpenChange={() => {}}>
        <DialogContent
          className="max-w-sm"
          onInteractOutside={(e) => e.preventDefault()}
        >
          <div className="flex flex-col items-center justify-center py-8">
            <div className="relative">
              <div className="w-16 h-16 border-4 border-gray-200 border-t-[#c9332C] rounded-full animate-spin"></div>
              <div className="absolute inset-0 flex items-center justify-center">
                <Ticket className="h-6 w-6 text-[#c9332C]" />
              </div>
            </div>
            <p className="mt-6 text-base font-semibold text-gray-900 text-center">
              Carregando cupons...
            </p>
            <p className="mt-2 text-sm text-gray-500 text-center">
              Por favor, aguarde
            </p>
          </div>
        </DialogContent>
      </Dialog>

      {/* Header */}
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-3xl font-bold tracking-tight flex items-center gap-2">
            <Ticket className="h-8 w-8" />
            Gestão de Cupons
          </h1>
          <p className="text-muted-foreground">Gerenciar cupons de desconto</p>
        </div>
        <Button onClick={handleCreateCoupon} size="lg">
          <Plus className="mr-2 h-5 w-5" />
          Novo Cupom
        </Button>
      </div>

      {/* Cards de Estatísticas */}
      <div className="grid gap-4 md:grid-cols-2 lg:grid-cols-4">
        <Card>
          <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
            <CardTitle className="text-sm font-medium">
              Total de Cupons
            </CardTitle>
            <Ticket className="h-4 w-4 text-muted-foreground" />
          </CardHeader>
          <CardContent>
            <div className="text-2xl font-bold">{stats.total}</div>
            <p className="text-xs text-muted-foreground">Cupons cadastrados</p>
          </CardContent>
        </Card>

        <Card>
          <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
            <CardTitle className="text-sm font-medium">Ativos</CardTitle>
            <Power className="h-4 w-4 text-green-600" />
          </CardHeader>
          <CardContent>
            <div className="text-2xl font-bold">{stats.active}</div>
            <p className="text-xs text-muted-foreground">Cupons válidos</p>
          </CardContent>
        </Card>

        <Card>
          <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
            <CardTitle className="text-sm font-medium">Inativos</CardTitle>
            <PowerOff className="h-4 w-4 text-gray-400" />
          </CardHeader>
          <CardContent>
            <div className="text-2xl font-bold">{stats.inactive}</div>
            <p className="text-xs text-muted-foreground">Cupons desativados</p>
          </CardContent>
        </Card>

        <Card>
          <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
            <CardTitle className="text-sm font-medium">Expirados</CardTitle>
            <Calendar className="h-4 w-4 text-red-600" />
          </CardHeader>
          <CardContent>
            <div className="text-2xl font-bold">{stats.expired}</div>
            <p className="text-xs text-muted-foreground">Cupons vencidos</p>
          </CardContent>
        </Card>
      </div>

      {/* Filtros e Busca */}
      <Card>
        <CardHeader>
          <div className="flex flex-col md:flex-row gap-4">
            <div className="relative flex-1">
              <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-gray-400" />
              <Input
                placeholder="Buscar por código ou descrição..."
                value={searchQuery}
                onChange={(e) => setSearchQuery(e.target.value)}
                className="pl-10"
              />
            </div>
            <Select value={filterStatus} onValueChange={setFilterStatus}>
              <SelectTrigger className="w-full md:w-48">
                <SelectValue placeholder="Filtrar por status" />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="all">Todos</SelectItem>
                <SelectItem value="active">Ativos</SelectItem>
                <SelectItem value="inactive">Inativos</SelectItem>
                <SelectItem value="expired">Expirados</SelectItem>
              </SelectContent>
            </Select>
          </div>
        </CardHeader>
        <CardContent>
          {!loading && filteredCoupons.length === 0 ? (
            <div className="text-center py-10 text-gray-500">
              {searchQuery || filterStatus !== "all"
                ? "Nenhum cupom encontrado com os filtros aplicados."
                : "Nenhum cupom cadastrado."}
            </div>
          ) : (
            <div className="overflow-x-auto">
              <Table>
                <TableHeader>
                  <TableRow>
                    <TableHead>Código</TableHead>
                    <TableHead>Descrição</TableHead>
                    <TableHead className="text-center">
                      <Percent className="h-4 w-4 inline mr-1" />
                      Desconto
                    </TableHead>
                    <TableHead>
                      <Calendar className="h-4 w-4 inline mr-1" />
                      Validade
                    </TableHead>
                    <TableHead className="text-center">Status</TableHead>
                    <TableHead className="text-right">Ações</TableHead>
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {filteredCoupons.map((coupon) => {
                    const statusBadge = couponService.getStatusBadge(coupon);
                    const isExpired = couponService.isExpired(
                      coupon.expiration_date
                    );

                    return (
                      <TableRow key={coupon.id}>
                        <TableCell>
                          <code className="text-sm bg-gray-100 px-2 py-1 rounded font-mono font-semibold">
                            {coupon.code}
                          </code>
                        </TableCell>
                        <TableCell className="max-w-xs truncate">
                          {coupon.description || "-"}
                        </TableCell>
                        <TableCell className="text-center font-semibold text-green-600">
                          {coupon.discount_percentage}%
                        </TableCell>
                        <TableCell>
                          <div className="text-sm">
                            {couponService.formatDate(coupon.expiration_date)}
                          </div>
                          {isExpired && (
                            <div className="text-xs text-red-600 font-medium">
                              Expirado
                            </div>
                          )}
                        </TableCell>
                        <TableCell className="text-center">
                          <Badge variant={statusBadge.variant}>
                            {statusBadge.label}
                          </Badge>
                        </TableCell>
                        <TableCell className="text-right">
                          <div className="flex justify-end gap-2">
                            <Button
                              variant="ghost"
                              size="sm"
                              onClick={() => handleToggleActive(coupon)}
                              title={coupon.active ? "Desativar" : "Ativar"}
                            >
                              {coupon.active ? (
                                <PowerOff className="h-4 w-4 text-orange-600" />
                              ) : (
                                <Power className="h-4 w-4 text-green-600" />
                              )}
                            </Button>
                            <Button
                              variant="ghost"
                              size="sm"
                              onClick={() => handleEditCoupon(coupon)}
                            >
                              <Edit className="h-4 w-4" />
                            </Button>
                            <Button
                              variant="ghost"
                              size="sm"
                              onClick={() => handleDeleteClick(coupon)}
                            >
                              <Trash2 className="h-4 w-4 text-red-600" />
                            </Button>
                          </div>
                        </TableCell>
                      </TableRow>
                    );
                  })}
                </TableBody>
              </Table>
            </div>
          )}
        </CardContent>
      </Card>

      {/* Modal de Formulário */}
      <CouponFormModal
        isOpen={showModal}
        onClose={() => {
          setShowModal(false);
          setEditingCoupon(null);
        }}
        onSave={handleSaveCoupon}
        coupon={editingCoupon}
        isSaving={savingCoupon}
      />

      {/* Modal de Confirmação de Exclusão */}
      <AlertDialog open={showDeleteDialog} onOpenChange={setShowDeleteDialog}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>Confirmar Exclusão</AlertDialogTitle>
            <AlertDialogDescription>
              Tem certeza que deseja excluir este cupom? Esta ação não pode ser
              desfeita.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel>Cancelar</AlertDialogCancel>
            <AlertDialogAction
              onClick={handleDeleteConfirm}
              className="bg-red-600 hover:bg-red-700"
            >
              Excluir
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </div>
  );
};

export default CouponsManagement;
